import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

/**
 * Phase 28 — Coordinated video playback in the feed.
 *
 * Goals:
 *  • Only ONE video plays at a time across the entire feed.
 *  • New video taking the spot pauses the previous one.
 *  • Mute preference is per-session (mute-by-default, Instagram-style).
 *
 * Usage:
 *   const { activeId, setActive, muted, toggleMute } = useVideoPlayer();
 *   useEffect(() => { if (activeId === post.id) videoRef.current.play(); }, [activeId]);
 */

const VideoPlayerContext = createContext(null);

export const VideoPlayerProvider = ({ children }) => {
  const [activeId, setActiveId] = useState(null);
  const [muted, setMuted] = useState(true); // mute-by-default for iOS autoplay
  const activeIdRef = useRef(null);

  const setActive = useCallback((id) => {
    activeIdRef.current = id;
    setActiveId(id);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => !m);
  }, []);

  const isActive = useCallback((id) => activeIdRef.current === id, []);

  return (
    <VideoPlayerContext.Provider value={{ activeId, setActive, isActive, muted, toggleMute }}>
      {children}
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayer = () => {
  const ctx = useContext(VideoPlayerContext);
  if (!ctx) {
    // Safe fallback when the provider is not mounted (e.g. isolated tests).
    return {
      activeId: null,
      setActive: () => {},
      isActive: () => false,
      muted: true,
      toggleMute: () => {},
    };
  }
  return ctx;
};
